import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import React from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { get } from "lodash";
import AdevalGallery from "app/adeval/adevalGallery";
import AdevalResult from "app/adeval/adevalResult";

function apiUrl(path: string): string {
  const host = window.location.hostname;
  const port = window.location.port;
  const base = port ? `//${host}:${port}` : `//${host}`;
  return `${base}/copilot/genesys/adeval${path}`;
}

const AdevalContainer = () => {
  const [searchParams] = useSearchParams();
  const [campaigns, setCampaigns] = React.useState<any[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = React.useState(true);
  const [scoring, setScoring] = React.useState(false);
  const [scoreResult, setScoreResult] = React.useState<any>(null);
  const [scoreError, setScoreError] = React.useState<string>("");

  const scoreIdx = searchParams.get("score");
  const viewIdx = searchParams.get("view");

  // Load campaigns on mount
  React.useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(apiUrl("/campaigns"));
        setCampaigns(get(res, "data.campaigns", []));
      } catch (e: any) {
        console.error("Failed to load campaigns", e);
      } finally {
        setLoadingCampaigns(false);
      }
    };
    load();
  }, []);

  const selectedAd = scoreIdx !== null ? campaigns[Number(scoreIdx)] : null;
  const viewAd = viewIdx !== null ? campaigns[Number(viewIdx)] : null;

  // Auto-score when opened with ?score=N
  React.useEffect(() => {
    if (!selectedAd || scoreResult || scoring) return;
    const run = async () => {
      setScoring(true);
      setScoreError("");
      try {
        const payload = {
          imageUrl: selectedAd.bgImageUrl || "",
          vertical: selectedAd.vertical || "",
          adSize: "300x250",
          brandName: selectedAd.campaignName || "",
          model: "gpt-4o",
          temperature: 0.2,
          refLimit: 5,
        };
        const res = await axios.post(apiUrl("/score/withrefs"), payload);
        setScoreResult(res.data);
      } catch (e: any) {
        setScoreError(e.message || "Scoring failed");
      } finally {
        setScoring(false);
      }
    };
    run();
  }, [selectedAd]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load saved evaluation when opened with ?view=N
  React.useEffect(() => {
    if (!viewAd || scoreResult) return;
    const load = async () => {
      setScoring(true);
      setScoreError("");
      try {
        const res = await axios.get(
          apiUrl(`/score/${encodeURIComponent(viewAd.campaignName)}`)
        );
        if (res.data && !res.data.error) {
          setScoreResult(res.data);
        } else {
          setScoreError("No saved evaluation found");
        }
      } catch (e: any) {
        setScoreError(e.message || "Failed to load evaluation");
      } finally {
        setScoring(false);
      }
    };
    load();
  }, [viewAd]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeAd = selectedAd || viewAd;

  const handleScore = (ad: any, idx: number) => {
    const url = `${window.location.pathname}?score=${idx}`;
    window.open(url, "_blank");
  };

  const handleView = (ad: any, idx: number) => {
    const url = `${window.location.pathname}?view=${idx}`;
    window.open(url, "_blank");
  };

  const handleClearScore = async (ad: any, idx: number) => {
    try {
      await axios.delete(apiUrl(`/score/${encodeURIComponent(ad.campaignName)}`));
      setCampaigns((prev) =>
        prev.map((c, i) =>
          i === idx
            ? { ...c, lastScore: undefined, lastPassed: undefined, lastEvaluatedAt: undefined }
            : c
        )
      );
    } catch (e: any) {
      console.error("Failed to clear score", e);
    }
  };

  return (
    <Box component="main" sx={{ display: "flex", flexDirection: "column" }}>
      <Paper sx={{ mt: "25px", p: 2, width: "100%" }}>
        <Grid container sx={{ mt: "10px", px: "10px" }}>
          <Grid item xs={12}>
            {(loadingCampaigns || scoring) && <LinearProgress />}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography variant="h4">
                {activeAd ? "Ad Evaluation" : "CreativeAI Ads"}
              </Typography>
            </Box>
          </Grid>

          {scoreError && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {scoreError}
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            {!activeAd ? (
              <AdevalGallery
                campaigns={campaigns}
                loading={loadingCampaigns}
                onScore={handleScore}
                onView={handleView}
                onClearScore={handleClearScore}
              />
            ) : (
              <AdevalResult
                ad={activeAd}
                result={scoreResult}
                scoring={scoring}
              />
            )}
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default AdevalContainer;
